import { __assign, __read } from "tslib";
import { FontIcon } from '@fluentui/react';
import { FileType } from '@office-start/document-types';
import { IconNames } from '@office-start/fabric-icon-subset-utility';
import { getKey, KeyboardKey } from '@office-start/keystroke-handler-utility';
import { FileIconType, getFileTypeIconProps } from '@uifabric/file-type-icons';
import classNames from 'classnames';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import styles from './CreateLinksControl.module.scss';
var createTilesUsingFileTypeIcon = new Set([FileType.List]);
export var CreateLinksControl = function (_a) {
    var _b, _c;
    var createLinkGroups = _a.createLinkGroups, createHeaderText = _a.createHeaderText, onTemplatesLinkClick = _a.onTemplatesLinkClick, templatesLinkText = _a.templatesLinkText, darkMode = _a.darkMode, useSvgIcons = _a.useSvgIcons;
    var createLinks = useMemo(function () {
        var links = [];
        createLinkGroups.map(function (group) {
            links = links.concat(group);
        });
        return links;
    }, [createLinkGroups]);
    var _d = __read(useState(undefined), 2), createIcons = _d[0], setCreateIcons = _d[1];
    useEffect(function () {
        if (!useSvgIcons) {
            return;
        }
        import(
        /* webpackChunkName: "create-links-control-icons" */ './CreateLinksControlIcons')
            .then(setCreateIcons)
            .catch(function () {
            /* Do nothing */
        });
    }, [useSvgIcons]);
    var linkRefs = useRef([]);
    var setActiveIndex = function (index) {
        if (index < createLinks.length &&
            linkRefs &&
            linkRefs.current &&
            linkRefs.current[index]) {
            linkRefs.current[index].focus();
        }
    };
    useEffect(function () {
        setActiveIndex(0);
    }, [createLinks, linkRefs]);
    var isValidIndex = function (groupIndex, linkIndex) {
        if (createLinkGroups &&
            createLinkGroups[groupIndex] &&
            createLinkGroups[groupIndex][linkIndex]) {
            return true;
        }
        return false;
    };
    var getIndex = function (groupIndex, linkIndex) {
        if (!isValidIndex(groupIndex, linkIndex)) {
            return 0;
        }
        var index = 0;
        for (var i = 0; i < groupIndex; i++) {
            index += createLinkGroups[i].length;
        }
        return index + linkIndex;
    };
    var supportedKeys = new Set([
        KeyboardKey.Tab,
        KeyboardKey.Enter,
        KeyboardKey.ArrowUp,
        KeyboardKey.ArrowDown,
    ]);
    var handleKey = function (event, groupIndex, linkIndex) {
        var key = getKey(event);
        var limit = createLinks.length;
        if (!supportedKeys.has(key)) {
            return;
        }
        event.preventDefault();
        switch (key) {
            case KeyboardKey.Enter:
                if (isValidIndex(groupIndex, linkIndex) &&
                    createLinkGroups[groupIndex][linkIndex].onClick) {
                    createLinkGroups[groupIndex][linkIndex].onClick();
                }
                break;
            case KeyboardKey.Tab:
                if (isValidIndex(groupIndex, linkIndex)) {
                    var index = getIndex(groupIndex, linkIndex);
                    setActiveIndex(event.shiftKey
                        ? (index - 1 + limit) % limit
                        : (index + 1) % limit);
                }
                break;
            case KeyboardKey.ArrowUp:
                if (isValidIndex(groupIndex, linkIndex)) {
                    setActiveIndex((getIndex(groupIndex, linkIndex) - 1 + limit) % limit);
                }
                break;
            case KeyboardKey.ArrowDown:
                if (isValidIndex(groupIndex, linkIndex)) {
                    setActiveIndex((getIndex(groupIndex, linkIndex) + 1) % limit);
                }
                break;
        }
    };
    return createLinkGroups &&
        createLinkGroups.length &&
        createLinkGroups[0] ? (React.createElement("div", { className: classNames(styles['create-links__container'], (_b = {},
            _b[styles['create-links--dark']] = darkMode,
            _b)) },
        React.createElement("div", { className: styles['create-links__header'] },
            React.createElement("h3", { className: classNames(styles['create-links__title'], (_c = {},
                    _c[styles['create-links--dark']] = darkMode,
                    _c)) }, createHeaderText),
            onTemplatesLinkClick && templatesLinkText && (React.createElement("div", { className: styles['create-links__templates-link'], onClick: onTemplatesLinkClick },
                React.createElement("div", { className: styles['create-links__templates-link-text'] }, templatesLinkText),
                useSvgIcons && createIcons ? (React.createElement(createIcons.ArrowRight16Regular, { primaryFill: "#d83b01", className: classNames(styles['create-links__forward-icon'], styles['create-links__forward-icon--svg']) })) : (React.createElement(FontIcon, { iconName: IconNames.Forward, className: styles['create-links__forward-icon'] }))))),
        React.createElement("ul", { className: styles['create-links__list'] }, createLinkGroups[0].map(function (createLink, linkIndex) {
            var _a, _b, _c;
            var _d;
            return (React.createElement("li", { className: classNames(styles['create-links__item'], (_a = {},
                    _a[styles['create-links--dark']] = darkMode,
                    _a)), onClick: createLink.onClick, ref: function (el) {
                    return (linkRefs.current[getIndex(0, linkIndex)] = el);
                }, onKeyDown: function (event) { return handleKey(event, 0, linkIndex); }, key: createLink.id, tabIndex: -1 },
                useSvgIcons && createIcons ? (React.createElement(createIcons.Add16Regular, { primaryFill: (_d = createLink.workload) === null || _d === void 0 ? void 0 : _d.color, className: styles['create-links__add-svg'] })) : (React.createElement(FontIcon, { iconName: IconNames.Add, className: styles['create-links__add-icon'], style: {
                        color: "".concat(createLink.workload
                            ? createLink.workload.color
                            : null),
                    } })),
                createTilesUsingFileTypeIcon.has(createLink.creationFileType) ? (React.createElement(FontIcon, __assign({}, getFileTypeIconProps({
                    type: FileIconType[createLink.creationFileType.toLowerCase()],
                    size: 32,
                    imageFileType: 'svg',
                }), { className: styles['create-links__icon'] }))) : !createLink.iconUrl ? (React.createElement("div", { className: classNames(createLink.icon, styles['create-links__icon']) })) : (React.createElement("img", { src: createLink.iconUrl, className: styles['create-links__icon'] })),
                React.createElement("div", { className: classNames(styles['create-links__item__title'], (_b = {},
                        _b[styles['create-links--dark']] = darkMode,
                        _b)) }, createLink.fileTypeNameLocalized),
                React.createElement("div", { className: classNames(styles['create-links__item__subtitle'], (_c = {},
                        _c[styles['create-links--dark']] = darkMode,
                        _c)) }, createLink.appName)));
        })))) : null;
};
//# sourceMappingURL=CreateLinksControl.js.map